// frontend/src/components/Pagination.jsx
import { useMemo } from "react";

/**
 * Google/AWS-style pagination component with smart page number rendering.
 *
 * Features:
 * - Always shows first and last page
 * - Shows 2 pages before/after current page
 * - Uses ellipsis (...) for gaps
 * - Responsive to different page counts
 * - Fully accessible with ARIA labels
 *
 * Examples:
 * - Small (≤7 pages): < 1 2 3 [4] 5 6 7 >
 * - Medium (8-10): < 1 2 ... 4 [5] 6 ... 9 10 >
 * - Large (>10): < 1 2 ... 8 [9] 10 ... 49 50 >
 *
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Callback when page changes: (pageNum) => void
 * @param {boolean} disabled - Disable all buttons (during loading)
 * @param {number} totalItems - Optional: Total item count for display
 */
export default function Pagination({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  disabled = false,
  totalItems = null,
  showTotal = true, // ✅ NEW: Optional prop to hide total display
}) {
  // ✅ SAFETY: Validate inputs
  const safeCurrent = Math.max(1, Math.min(currentPage || 1, totalPages || 1));
  const safeTotal = Math.max(1, totalPages || 1);

  /**
   * Generate array of page numbers to display with ellipsis.
   * Algorithm follows Google Cloud Console pattern.
   */
  const pageNumbers = useMemo(() => {
    // Edge case: only 1 page
    if (safeTotal <= 1) {
      return [1];
    }

    // Small datasets: show all pages (≤7 pages)
    if (safeTotal <= 7) {
      return Array.from({ length: safeTotal }, (_, i) => i + 1);
    }

    // Large datasets: smart rendering with ellipsis
    const pages = [];
    const ellipsis = "...";

    // Always show first page
    pages.push(1);

    // Calculate range around current page
    const showLeft = safeCurrent > 3; // Show ellipsis on left
    const showRight = safeCurrent < safeTotal - 2; // Show ellipsis on right

    if (!showLeft) {
      // Current page is near start: 1 2 3 [4] ... 50
      for (let i = 2; i <= Math.min(5, safeTotal - 1); i++) {
        pages.push(i);
      }
      if (safeTotal > 5) {
        pages.push(ellipsis);
        pages.push(safeTotal);
      }
    } else if (!showRight) {
      // Current page is near end: 1 ... 47 [48] 49 50
      pages.push(ellipsis);
      for (let i = Math.max(2, safeTotal - 4); i <= safeTotal; i++) {
        pages.push(i);
      }
    } else {
      // Current page is in middle: 1 ... 8 [9] 10 ... 50
      pages.push(ellipsis);

      // Show 2 pages before and after current
      for (let i = safeCurrent - 1; i <= safeCurrent + 1; i++) {
        if (i > 1 && i < safeTotal) {
          pages.push(i);
        }
      }

      pages.push(ellipsis);
      pages.push(safeTotal);
    }

    return pages;
  }, [safeCurrent, safeTotal]);

  /**
   * Handle page change with validation
   */
  const handlePageClick = (page) => {
    if (disabled || !onPageChange || typeof page !== "number") return;
    if (page < 1 || page > safeTotal || page === safeCurrent) return;

    try {
      onPageChange(page);
    } catch (error) {
      console.error("[Pagination] Error in onPageChange callback:", error);
    }
  };

  /**
   * Handle Prev/Next buttons
   */
  const handlePrev = () => {
    if (safeCurrent > 1) {
      handlePageClick(safeCurrent - 1);
    }
  };

  const handleNext = () => {
    if (safeCurrent < safeTotal) {
      handlePageClick(safeCurrent + 1);
    }
  };

  // Don't render if no pages
  if (safeTotal <= 0) {
    return null;
  }

  return (
    <div className="table-footer">
      {/* Left side: Total count info */}
      {showTotal && (
        <div className="kv">
          {totalItems != null && (
            <>
              Total: <b>{totalItems}</b>
              {" • "}
            </>
          )}
          Page <b>{safeCurrent}</b>
          {safeTotal > 1 && (
            <>
              {" of "}
              <b>{safeTotal}</b>
            </>
          )}
        </div>
      )}
      {!showTotal && <div />} {/* Spacer to maintain flex layout */}
      {/* Right side: Page navigation */}
      <div className="pager">
        {/* Previous button */}
        <button onClick={handlePrev} disabled={disabled || safeCurrent <= 1} aria-label="Previous page" title="Previous page">
          Prev
        </button>

        {/* Page numbers */}
        {pageNumbers.map((pageNum, index) => {
          // Ellipsis (non-clickable)
          if (pageNum === "...") {
            return (
              <span
                key={`ellipsis-${index}`}
                className="page-ellipsis"
                style={{
                  padding: "0 8px",
                  color: "var(--text-tertiary, #666)",
                  userSelect: "none",
                }}>
                ...
              </span>
            );
          }

          // Page number button
          const isCurrentPage = pageNum === safeCurrent;
          return (
            <button
              key={pageNum}
              onClick={() => handlePageClick(pageNum)}
              disabled={disabled || isCurrentPage}
              aria-label={`Page ${pageNum}`}
              aria-current={isCurrentPage ? "page" : undefined}
              className={isCurrentPage ? "active" : ""}
              style={{
                fontWeight: isCurrentPage ? 600 : 400,
                backgroundColor: isCurrentPage ? "var(--accent-primary, #0066cc)" : undefined,
                color: isCurrentPage ? "white" : undefined,
                minWidth: "36px",
              }}>
              {pageNum}
            </button>
          );
        })}

        {/* Next button */}
        <button onClick={handleNext} disabled={disabled || safeCurrent >= safeTotal} aria-label="Next page" title="Next page">
          Next
        </button>
      </div>
    </div>
  );
}
